/**
 * SC-01 — the current slide, as context for a chat turn (AIVA-6809).
 *
 * Read fresh when the user sends, so it always describes the slide and the
 * selection at that moment. Without it the AI could only ask the user to paste
 * the slide text, and that request was then offered as something to "Insert
 * into slide".
 *
 * `text` is always every shape's text on the current slide. The selection goes
 * beside it in `selectedText`, most specific first:
 *  1. text highlighted inside a shape (the cursor is in a text box);
 *  2. otherwise the text of the selected shapes.
 * The slide is never replaced by the selection: with the cursor merely sitting
 * in a word, PowerPoint reports that word as the selected text, and a turn that
 * sent only it ("many") left "summarise the slide" with nothing to summarise.
 * `hasSelection` says a selection was sent, so the prompt can tell them apart.
 *
 * Returned in the same shape as Word's document context so it rides the same
 * envelope — and gets the same untrusted-content screening — on the backend.
 *
 * Never throws and never blocks a turn: every read is optional, and anything
 * the host cannot serve is skipped. `null` means "send no slide context", which
 * is exactly how a PowerPoint turn behaved before this existed.
 */

import type { WordDocumentContext } from '../../../../integrations/tools/word/types';

export interface SlideTurnContext extends WordDocumentContext {
  /** The selected text or shapes, when there is a selection; `text` is the whole slide. */
  selectedText?: string;
  /** The current slide's 1-based position, when the host reports it. */
  slideNumber?: number;
  slideCount?: number;
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

/** PowerPoint separates paragraphs with CR (and vertical tab inside one). */
function normalise(text: string): string {
  return text.replace(/\r\n?|\v/g, '\n').trim();
}

/** The file name the user would recognise, from the document URL. */
export function presentationName(url: string | undefined): string {
  if (!url) return '';
  const last = url.split(/[\\/]/).pop() ?? '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

function joinTexts(texts: readonly string[]): string {
  return texts.map(normalise).filter(Boolean).join('\n');
}

/**
 * Assemble the turn context from whatever was read. Exported for tests.
 *
 * `texts` is the whole slide and `selectedTexts` the selection. When the
 * slide's shapes yield nothing but the selection does, the selection becomes
 * `text`, so the turn keeps what context it has.
 */
export function buildSlideTurnContext(input: {
  name: string;
  texts: readonly string[];
  selectedTexts?: readonly string[];
  slideNumber?: number;
  slideCount?: number;
}): SlideTurnContext | null {
  const slide = joinTexts(input.texts);
  const selection = joinTexts(input.selectedTexts ?? []);
  const text = slide || selection;
  if (!text) return null;
  return {
    name: input.name,
    text,
    wordCount: text.split(/\s+/).filter(Boolean).length,
    charCount: text.length,
    hasSelection: Boolean(selection),
    ...(selection && slide ? { selectedText: selection } : {}),
    ...(input.slideNumber ? { slideNumber: input.slideNumber } : {}),
    ...(input.slideCount ? { slideCount: input.slideCount } : {}),
  };
}

/** One shape's text, or "" when it has no text frame (a picture, chart or line). */
async function readShapeText(context: PowerPoint.RequestContext, shape: PowerPoint.Shape): Promise<string> {
  try {
    const range = shape.textFrame.textRange;
    range.load('text');
    await context.sync();
    return range.text ?? '';
  } catch {
    return ''; // no text frame on this shape
  }
}

/**
 * Each shape's text, read one at a time: pictures, charts and lines have no
 * text frame, and asking one for its text fails the whole batch — so the reads
 * must not share a sync. Chained rather than awaited in a loop, which keeps
 * them strictly one after another on the shared request context.
 */
async function readShapeTexts(
  context: PowerPoint.RequestContext,
  shapes: readonly PowerPoint.Shape[]
): Promise<string[]> {
  const texts: string[] = [];
  await shapes.reduce(
    (previous, shape) =>
      previous.then(async () => {
        const text = await readShapeText(context, shape);
        if (text.trim()) texts.push(text);
      }),
    Promise.resolve()
  );
  return texts;
}

/** Text highlighted inside a shape, or "" when the cursor is not in text. */
async function readHighlightedText(context: PowerPoint.RequestContext): Promise<string> {
  try {
    const range = context.presentation.getSelectedTextRangeOrNullObject();
    range.load('text');
    await context.sync();
    return range.isNullObject ? '' : (range.text ?? '');
  } catch {
    return ''; // PowerPointApi 1.5 not available on this host
  }
}

/** The selected shapes' text, or [] when no shape is selected. */
async function readSelectedShapeTexts(context: PowerPoint.RequestContext): Promise<string[]> {
  try {
    const shapes = context.presentation.getSelectedShapes();
    shapes.load('items');
    await context.sync();
    return await readShapeTexts(context, shapes.items);
  } catch {
    return []; // PowerPointApi 1.5 not available on this host
  }
}

/**
 * Whether the user has a selection with text right now: text highlighted
 * inside a shape, or selected shapes that carry text. The same rule as the
 * turn's `hasSelection`, so the action bar's Replace selection (SC-03) is
 * enabled by exactly what the send-time read would have enabled it for.
 *
 * G-10 (AIVA-8913): re-read on every selection change, so a shape selected
 * AFTER the reply arrived enables the latest bar. Never throws: a host that
 * can't report the selection reads as "nothing selected".
 */
export async function isTextSelectionPresent(): Promise<boolean> {
  if (!hasPpt()) return false;
  try {
    return await ppt().run(async (context) => {
      if ((await readHighlightedText(context)).trim()) return true;
      return (await readSelectedShapeTexts(context)).length > 0;
    });
  } catch {
    return false;
  }
}

/**
 * The current slide as context for the turn being sent, or null when there is
 * nothing to send (not in PowerPoint, no slide selected, an empty slide, or a
 * host that cannot read slides).
 */
export async function readSlideContextForTurn(): Promise<SlideTurnContext | null> {
  if (!hasPpt()) return null;
  try {
    return await ppt().run(async (context) => {
      const slides = context.presentation.slides;
      slides.load('items/id');
      const selected = context.presentation.getSelectedSlides();
      selected.load('items/id');
      await context.sync();
      const slide = selected.items[0];
      if (!slide) return null;

      const index = slides.items.findIndex((s) => s.id === slide.id);
      const position = {
        slideNumber: index >= 0 ? index + 1 : undefined,
        slideCount: slides.items.length || undefined,
      };
      const name = presentationName(Office.context?.document?.url);

      const highlighted = await readHighlightedText(context);
      const selectedTexts = highlighted.trim() ? [highlighted] : await readSelectedShapeTexts(context);

      slide.shapes.load('items');
      await context.sync();
      const slideTexts = await readShapeTexts(context, slide.shapes.items);
      return buildSlideTurnContext({ name, texts: slideTexts, selectedTexts, ...position });
    });
  } catch {
    return null;
  }
}
